import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  LocationOn as LocationOnIcon,
  Close as CloseIcon,
  RotateRight as RotateRightIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { fieldInspectionService } from '../../services/fieldInspectionService';
import ImageViewer from '../common/ImageViewer';
import GoogleMapIframe from '../GoogleMapIframe';

const REPORT_STATUS_COLORS = {
  'new': 'info',
  'assigned': 'warning', 
  'in_progress': 'primary',
  'uploaded': 'primary',
  'verified': 'success',
  'rejected': 'error',
};

const REPORT_STATUS_LABELS = {
  'new': 'New Request',
  'assigned': 'Assigned',
  'in_progress': 'In Progress',
  'uploaded': 'Uploaded',
  'verified': 'Verified',
  'rejected': 'Rejected',
};

const REPORT_STATUS_ICONS = {
  'new': <AssignmentIcon />,
  'assigned': <PersonIcon />,
  'in_progress': <RotateRightIcon />,
  'uploaded': <UploadIcon />,
  'verified': <CheckIcon />,
  'rejected': <CancelIcon />,
};

const LIVESTOCK_TYPES = ['large_milch_animals', 'small_milch_animals', 'large_draught_animals', 'small_draught_animals', 'poultry'];

const ReportDetailsDialog = ({
  open,
  onClose,
  selectedReport,
  reportActions,
  formatDate,
  getReportStatus,
}) => {
  const theme = useTheme();
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState(null);
  const [googleMapIframeOpen, setGoogleMapIframeOpen] = useState(false);
  const [selectedReportDamageAssessment, setSelectedReportDamageAssessment] = useState({});

  useEffect(() => {
    if (selectedReport?.images_uploaded && selectedReport?.captured_images && selectedReport.captured_images.length > 0) {
      loadGalleryImages(selectedReport.captured_images);
    } else {
      setGalleryImages([]);
    }
  }, [selectedReport?.id]);

  useEffect(() => {
    if (selectedReport?.id) {
      const damageAssessment = {
        land_damage_report: selectedReport.land_damage_report,
        crop_damage_report: selectedReport.crop_damage_report,
        livestock_damage_report: selectedReport.livestock_damage_report,
        house_damage_report: selectedReport.house_damage_report
      };
      setSelectedReportDamageAssessment(damageAssessment);
    }
  }, [selectedReport?.id]);

  const loadGalleryImages = async (capturedImages) => {
    if (!capturedImages || capturedImages.length === 0) {
      setGalleryImages([]);
      return;
    }
    
    setGalleryLoading(true);
    setGalleryError(null);
    
    try {
      console.log('Loading images:', capturedImages);
      
      const images = await Promise.all(
        capturedImages.map(async (imagePath) => {
          try {
            const imageBlob = await fieldInspectionService.getUploadedImage(imagePath.image_path);
            const objectUrl = URL.createObjectURL(imageBlob);
            
            return {
              original: objectUrl,
              thumbnail: objectUrl,
              description: `${imagePath.location_description || 'No description'} | Lat: ${imagePath.latitude}, Lng: ${imagePath.longitude} | ${new Date(imagePath.timestamp).toLocaleString()}`,
              originalAlt: imagePath.location_description || 'Inspection image',
              objectUrl: objectUrl,
            };
          } catch (error) {
            console.error('Error loading individual image:', error);
            return null;
          }
        })
      );
      
      const validImages = images.filter(img => img !== null);
      setGalleryImages(validImages);
      
      if (validImages.length === 0) {
        setGalleryError('No images could be loaded');
      }
      
    } catch (error) {
      console.error('Error loading images:', error);
      setGalleryError('Failed to load images');
      setGalleryImages([]);
    } finally {
      setGalleryLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      galleryImages.forEach(image => {
        if (image.objectUrl) {
          URL.revokeObjectURL(image.objectUrl);
        }
      });
    };
  }, [galleryImages]);

  const handleDialogClose = () => {
    galleryImages.forEach(image => {
      if (image.objectUrl) {
        URL.revokeObjectURL(image.objectUrl);
      }
    });
    setGalleryImages([]);
    setGalleryLoading(false);
    setGalleryError(null);
    onClose();
  };

  const formatSnakeCaseToTitle = (str) => {
    if (!str) return '';
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  if (!selectedReport) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            zIndex: 1300, // Lower than PhotoView z-index
          },
          zIndex: 1300,
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="h6" component="span">
              #{selectedReport.id} - Inspection Report Details
            </Typography>
          </Box>
          <IconButton
            edge="end"
            size="large"
            variant="outlined"
            color="inherit"
            onClick={handleDialogClose}
          >
            <CloseIcon fontSize="inherit"/>
          </IconButton>
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'end', borderBottom: 1 }}>
          {reportActions && reportActions(selectedReport)}
        </DialogActions>
        <DialogContent sx={{ backgroundColor: '#f7f7fa', px: 3, py: 2 }}>
          <>
            <Box sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs:12, md: 6}} sx={{ backgroundColor: 'primary.main', borderRadius: 1, p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                    Applicant's Brief Desc. of the Incident
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'white' }}>
                    {selectedReport.application_details.remarks || 'No remarks provided by the applicant.'}
                  </Typography>
                </Grid>
                <Grid size={{ xs:12, md: 6}} sx={{ backgroundColor: 'primary.main', borderRadius: 1, p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'tomato' }}>
                    Inspection Report Remarks
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'white' }}>
                    {selectedReport.brief_remarks || '--'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Report ID
                      </Typography>
                      <Typography variant="body2" paragraph>
                        #{selectedReport.id}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Application Refno.
                      </Typography>
                      <Typography variant="body2" paragraph>
                        #{selectedReport.application_details.application_refno}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Images Uploaded
                      </Typography>
                      <Chip
                        label={selectedReport.images_uploaded ? 'Yes' : 'No'}
                        color={selectedReport.images_uploaded ? 'info' : 'default'}
                        icon={selectedReport.images_uploaded ? <CheckIcon /> : <CloseIcon />}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Grid>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Status
                      </Typography>
                      <Chip
                        label={REPORT_STATUS_LABELS[getReportStatus(selectedReport)]}
                        color={REPORT_STATUS_COLORS[getReportStatus(selectedReport)]}
                        icon={REPORT_STATUS_ICONS[getReportStatus(selectedReport)]}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Grid>
                    <Grid size={{ xs:12, md: 6}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Requested by
                      </Typography>
                      <Typography variant="body2">
                        {selectedReport.field_head_official.full_name}, {selectedReport.field_head_official.role_name}
                      </Typography>
                      <Typography variant="body2">
                        {selectedReport.field_head_official.designation}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 6}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Assigned To
                      </Typography>
                      <Typography variant="body2">
                        {selectedReport.field_assigned_user?.full_name || 'Unassigned'}
                      </Typography>
                      <Typography variant="body2">
                        {selectedReport.field_assigned_user?.role.full_name || 'Unassigned'}
                      </Typography>
                    </Grid>
                    <Grid 
                      size={{ xs:12, md: 6}} 
                      hover='true'
                      sx={{ 
                        border: '5px solid #e0e0e0', 
                        borderRadius: 1, 
                        p: 2, 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'white',
                          borderRadius: 2,
                          transform: 'translateY(-8px)',
                          boxShadow: theme.shadows[8],
                        }
                      }}
                      onClick={() => setGoogleMapIframeOpen(true)}
                    >
                      <LocationOnIcon fontSize="inherit" />
                      <Typography variant="subtitle2" gutterBottom>
                        Location
                      </Typography>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: 'primary.main',
                        }}
                      >
                        [Click to view on map]
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedReport.location || 'Not specified'}
                      </Typography>
                      <Typography variant="subtitle2">
                        Altitude: {parseFloat(selectedReport.loc_altitude).toFixed(3) + ' m' || 'Not Specified'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 6}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Location Description
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedReport.location_desc || 'No description provided'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Inspection Date
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {formatDate(selectedReport.inspection_datetime)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 6 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Brief Remarks
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedReport.brief_remarks || 'No remarks provided'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, md: 3}} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Verif. Conducted With
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedReport.verif_conducted_with || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Image Gallery Section */}
              {selectedReport.images_uploaded && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h5" gutterBottom>
                        Inspection Images
                        {galleryImages.length > 0 && ` (${galleryImages.length})`}
                      </Typography>
                      
                      {galleryLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                            Loading images...
                          </Typography>
                        </Box>
                      )}
                      
                      {galleryError && (
                        <Alert severity="error" sx={{ my: 2 }}>
                          {galleryError}
                          <Button 
                            variant="text" startIcon={<RefreshIcon />} 
                            onClick={
                              () => loadGalleryImages(selectedReport.captured_images)
                            } 
                            sx={{ ml: 2 }}
                          >
                            Retry
                          </Button>
                        </Alert>
                      )}
                      
                      {!galleryLoading && !galleryError && galleryImages.length === 0 && (
                        <Alert severity="info" sx={{ my: 2 }}>
                          No images available for this inspection report.
                        </Alert>
                      )}
                      
                      {!galleryLoading && galleryImages.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Grid container spacing={2} className="image-grid-container">
                            {galleryImages.map((item, index) => (
                              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                                <Box sx={{ position: 'relative' }}>
                                  <ImageViewer
                                    imageUrl={item.original}
                                    alt={item.originalAlt || `Inspection Image ${index + 1}`}
                                    title={`Image ${index + 1} of ${galleryImages.length}`}
                                    description={item.description || 'Inspection image'}
                                    downloadFilename={`inspection-image-${index + 1}.jpg`}
                                    thumbnailWidth="100%"
                                    thumbnailHeight="140px"
                                  />
                                  
                                  {/* Image Number Badge */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                      color: 'white',
                                      borderRadius: 2,
                                      px: 1,
                                      py: 0.5,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      zIndex: 2
                                    }}
                                  >
                                    {index + 1}/{galleryImages.length}
                                  </Box>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
              
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Typography variant='h4'>
                  Damage Assessment
                </Typography>
                <Grid size={{ xs:12 }}>
                  {Object.entries(selectedReportDamageAssessment).map(([key, value]) => (
                    <Grid key={key} container spacing={2}>
                      { key === 'land_damage_report' && value !== null && (
                        <Card variant="outlined" sx={{ mt: 2 }}>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid size={{xs: 12}}>
                                <Typography variant='h5'>
                                  Land Damage Report
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Owner or Tenant
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.owner_or_tenant || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Domicile/SSC Holder
                                </Typography>
                                  {value.is_domicile_ssc_holder 
                                    ? (
                                      <Typography variant="body2" sx={{ backgroundColor: 'green', color: 'white', p: 1, borderRadius: 2, fontWeight: 400 }}>
                                        <CheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Yes
                                      </Typography>
                                    )
                                    : (
                                      <Typography variant="body2" sx={{ backgroundColor: 'red', color: 'white', p: 1, borderRadius: 2 }}>
                                        <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        No
                                      </Typography>
                                    )
                                  }
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Domicile/SSC ID
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.ssc_domicile_number || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Areas Affected (ha)
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.area_affected || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Plot Nos
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.plot_nos || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Damage Description
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.damage_description || 'Not specified'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Damage Date Time
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {formatDate(value.damage_date_time) || 'Not specified'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                      {
                        key === 'house_damage_report' && value !== null && (
                          <Card variant="outlined" sx={{ mt: 2 }}>
                            <CardContent>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                  <Typography variant='h5'>
                                    House Damage Report
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs:12, md:4 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Fully Damaged
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {value.fully_damaged || 'None'}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs:12, md:4 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Severely Damaged
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {value.severely_damaged || 'None'}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs:12, md:4 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Partially Damaged
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {value.partially_damaged || 'None'}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Hut Damaged
                                  </Typography>
                                  {
                                    value.hut_damaged
                                    ? (
                                      <Typography variant="body2" sx={{ backgroundColor: 'green', color: 'white', p: 1, borderRadius: 2, fontWeight: 400 }}>
                                        <CheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Yes
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ backgroundColor: 'red', color: 'white', p: 1, borderRadius: 2 }}>
                                        <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        No
                                      </Typography>
                                    )
                                  }
                                </Grid>

                                <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Cattle Shed Damaged
                                  </Typography>
                                  {
                                    value.cattle_shed_damaged
                                    ? (
                                      <Typography variant="body2" sx={{ backgroundColor: 'green', color: 'white', p: 1, borderRadius: 2, fontWeight: 400 }}>
                                        <CheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Yes
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ backgroundColor: 'red', color: 'white', p: 1, borderRadius: 2 }}>
                                        <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        No
                                      </Typography>
                                    )
                                  }
                                </Grid>
                                <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Clothing loss
                                  </Typography>
                                  {
                                    value.clothing_loss
                                    ? (
                                      <Typography variant="body2" sx={{ backgroundColor: 'green', color: 'white', p: 1, borderRadius: 2, fontWeight: 400 }}>
                                        <CheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Yes
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ backgroundColor: 'red', color: 'white', p: 1, borderRadius: 2 }}>
                                        <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        No
                                      </Typography>
                                    )
                                  }
                                </Grid>
                                <Grid size={{ xs:12, md:3 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Utensil loss
                                  </Typography>
                                  {
                                    value.utensil_loss
                                    ? (
                                      <Typography variant="body2" sx={{ backgroundColor: 'green', color: 'white', p: 1, borderRadius: 2, fontWeight: 400 }}>
                                        <CheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        Yes
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ backgroundColor: 'red', color: 'white', p: 1, borderRadius: 2 }}>
                                        <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                        No
                                      </Typography>
                                    )
                                  }
                                </Grid>


                                <Grid size={{ xs:12, md:4 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Cause of Damage
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {value.cause_of_damage || 'Not specified'}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs:12, md:4 }}  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Damage Date Time
                                  </Typography>
                                  <Typography variant="body2" paragraph>
                                    {formatDate(value.damage_date_time) || 'Not specified'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        )
                      }
                      { key === 'livestock_damage_report' && value !== null && (
                        <Card variant="outlined" sx={{ mt: 2 }}>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12 }}>
                                <Typography variant='h5'>
                                  Livestock Damage Report
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, md: 4 }}>
                                <TableContainer component={Paper}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell colSpan={2} align="center" sx={{ backgroundColor: '#f0f0f0' }}>
                                          Livestock Counts
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Nos.</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {
                                        Object.entries(value || {}).map(([livestockType, count]) => {
                                          if(!LIVESTOCK_TYPES.includes(livestockType) || count === null || count < 1) {
                                              return null;
                                          }
                                          return (
                                              <TableRow key={livestockType}>
                                                  <TableCell>{formatSnakeCaseToTitle(livestockType)}</TableCell>
                                                  <TableCell>{count !== null && count !== undefined ? count : 'N/A'}</TableCell>
                                              </TableRow>
                                          );
                                        })
                                      }
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Cause of Death / Damage
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.cause_of_death || 'Not specified'}
                                </Typography>
                              </Grid>
                              
                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Damage Description
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.damage_description || 'Not specified'}
                                </Typography>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Damage Date Time
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {formatDate(value.damage_date_time) || 'Not specified'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                      { key === 'crop_damage_report' && value !== null && (
                        <Card variant="outlined" sx={{ mt: 2 }}>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid size={{ xs:12 }}>
                                <Typography variant='h5'>
                                  Crop Damage Report
                                </Typography>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Crop Damaged
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.crop_damaged || 'Not specified'}
                                </Typography>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Approx Value of Crops (INR)
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.approx_value_of_crops !== undefined && value.approx_value_of_crops !== null ? (
                                    `Rs. ${Number(value.approx_value_of_crops).toLocaleString('en-IN')}`
                                  ) : 'Not specified'}
                                </Typography>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Cause of Damage
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {value.cause_of_damage || 'Not specified'}
                                </Typography>
                              </Grid>

                              <Grid size={{ xs:12, md:4 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Damage Date Time
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {formatDate(value.damage_date_time) || 'Not specified'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Typography variant="h4">
                  Application Details
                </Typography>
                <Grid size={{ xs:12}}>
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      {selectedReport.application_details && (
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Application Ref No.</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.application_refno || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Applicant Name</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.applicant_name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Father's/Husband's Name</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.sodowo || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Voter ID</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.voter_id || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Phone Number</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ph_number || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2">Actual Calamity Date</Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.actual_nc_datetime
                                ? formatDate(selectedReport.application_details.actual_nc_datetime)
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Damage Type
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {(selectedReport.application_details.damage_type || []).join(', ') || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Land Khatian Number
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.land_khatian_number || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Land Plot Nos
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {(selectedReport.application_details.land_plot_nos || []).join(', ') || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Village
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.village || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Ward
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ward?.name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              GPU
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ward?.gpu?.name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Block
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ward?.gpu?.block?.name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Subdivision
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ward?.gpu?.block?.subdivision?.name || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              District
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.ward?.gpu?.block?.subdivision?.district?.name || 'N/A'} | 
                              {selectedReport.application_details.district || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 3 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              State
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.state || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Remarks
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {selectedReport.application_details.remarks || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </>
        </DialogContent>
      </Dialog>

      {/* Google Map Iframe */}
      <Dialog
        open={googleMapIframeOpen}
        onClose={() => setGoogleMapIframeOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
        >
          <Box>
            <Typography variant="h6">
              Location - [{selectedReport?.location}]
            </Typography>
            <Divider />
            <Typography variant="subtitle2" sx={{ ml: 2 }}>
              {selectedReport?.location_desc || ''}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            size="large"
            variant="outlined"
            color="inherit"
            onClick={() => setGoogleMapIframeOpen(false)}
          >
            <CloseIcon fontSize="inherit"/>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <GoogleMapIframe
            lat={selectedReport?.location?.split(',')[0]}
            lng={selectedReport?.location?.split(',')[1]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportDetailsDialog;
